import { Box, Chip, useTheme, type Theme } from "@mui/material";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { SectionCard } from "@/components/DesignSystem";
import { colors, eventTypeColors } from "@/theme/eventsTokens";
import type { TimeseriesPoint } from "@/types";
import {
  useTimeSeriesState,
  LINE_SERIES,
  PRESETS,
  type SeriesKey,
  type SeriesDef,
  type DayBoundary,
} from "./useTimeSeriesState.ts";

interface AuthTimeSeriesProps {
  readonly data: TimeseriesPoint[];
  readonly auditMode: boolean;
}

const SERIES_COLORS: Record<string, string> = {
  ...eventTypeColors,
  // Audit-mode counterpart to "Blocked"; the two share one stack slot and never
  // render together (XOR), so they share the red — the legend label distinguishes them.
  would_block: eventTypeColors.blocked,
  queries: colors.accent,
};

function ensureUtc(iso: string): string {
  if (!/[Z+-]\d{0,4}$/.test(iso)) return iso + "Z";
  return iso;
}

function formatTime(iso: string): string {
  const d = new Date(ensureUtc(iso));
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(iso: string): string {
  const d = new Date(ensureUtc(iso));
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatDateTime(iso: string): string {
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function AuthTimeSeries({ data, auditMode }: AuthTimeSeriesProps) {
  const state = useTimeSeriesState(data, auditMode);
  if (!data.length) return null;

  return (
    <SectionCard title="Activity History">
      <PresetBar state={state} />
      <ChartArea state={state} />
    </SectionCard>
  );
}

function PresetBar({ state }: { readonly state: ReturnType<typeof useTimeSeriesState> }) {
  const theme = useTheme();
  const isCustom = state.activePreset === "";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
      {PRESETS.map((p) => (
        <Chip
          key={p.label}
          label={p.label === "All" ? state.allLabel : p.label}
          size="small"
          variant={state.activePreset === p.label ? "filled" : "outlined"}
          color={state.activePreset === p.label ? "primary" : "default"}
          onClick={() => state.applyPreset(p.label)}
          sx={{ fontSize: 11, height: 24 }}
        />
      ))}
      {isCustom && (
        <>
          <Chip
            label="Reset"
            size="small"
            variant="outlined"
            onClick={() => state.applyPreset(state.lastPreset)}
            onDelete={() => state.applyPreset(state.lastPreset)}
            sx={{ fontSize: 11, height: 24 }}
          />
          {state.sliced.length >= 2 && (
            <Box
              component="span"
              sx={{ fontSize: 11, color: theme.palette.text.primary, fontWeight: 500 }}
            >
              {formatTime(state.sliced[0].timestamp)} — {formatTime(state.sliced[state.sliced.length - 1].timestamp)}
            </Box>
          )}
        </>
      )}
      <Box
        component="span"
        sx={{ fontSize: 11, color: theme.palette.text.secondary, ml: "auto", display: "flex", gap: 1 }}
      >
        {state.granularity && (
          <Box component="span" sx={{ opacity: 0.7 }}>
            {state.granularity} intervals
          </Box>
        )}
        <Box component="span">Drag on chart to zoom</Box>
      </Box>
    </Box>
  );
}

function DayOverlay({ label, theme }: { readonly label: string; readonly theme: Theme }) {
  return (
    <Box
      component="span"
      sx={{
        position: "absolute",
        top: 6,
        left: 60,
        fontSize: 10,
        color: theme.palette.text.secondary,
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {label}
    </Box>
  );
}

function ChartArea({ state }: { readonly state: ReturnType<typeof useTimeSeriesState> }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: "relative",
        userSelect: "none",
        "& *:focus, & *:focus-visible": { outline: "none" },
        "& .recharts-wrapper": { cursor: "crosshair" },
      }}
    >
      {state.visibleStartLabel && <DayOverlay label={state.visibleStartLabel} theme={theme} />}
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart
          data={state.chartData}
          onMouseDown={state.onMouseDown as never}
          onMouseMove={state.onMouseMove as never}
          onMouseUp={state.onMouseUp as never}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <ChartAxes theme={theme} yMax={state.yMax} />
          <Tooltip
            labelFormatter={(label) => formatDateTime(String(label))}
            formatter={(value) => formatCompact(Number(value))}
            contentStyle={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              color: theme.palette.text.primary,
            }}
          />
          <Legend content={<SeriesLegend series={state.series} hidden={state.hidden} onToggle={state.toggleSeries} />} />
          <StackedAreas series={state.series} hidden={state.hidden} />
          <Line
            type="monotone"
            dataKey={LINE_SERIES.key}
            name={LINE_SERIES.label}
            stroke={SERIES_COLORS[LINE_SERIES.key]}
            strokeWidth={2}
            dot={false}
            hide={state.hidden.has(LINE_SERIES.key)}
          />
          <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeWidth={1} />
          <DayBoundaryLines boundaries={state.dayBoundaries} theme={theme} />
          {state.selStart && state.selEnd && (
            <ReferenceArea
              x1={state.selStart}
              x2={state.selEnd}
              fill={theme.palette.primary.main}
              fillOpacity={0.15}
              stroke={theme.palette.primary.main}
              strokeOpacity={0.4}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

function ChartAxes({ theme, yMax }: { readonly theme: Theme; readonly yMax: number }) {
  return (
    <>
      <XAxis
        dataKey="timestamp"
        tickFormatter={formatTime}
        stroke={theme.palette.text.secondary}
        fontSize={11}
        minTickGap={60}
      />
      <YAxis
        domain={[0, yMax]}
        allowDecimals={false}
        tickFormatter={formatCompact}
        stroke={theme.palette.text.secondary}
        fontSize={11}
        width={50}
      />
    </>
  );
}

function StackedAreas({ series, hidden }: { readonly series: readonly SeriesDef[]; readonly hidden: Set<SeriesKey> }) {
  return (
    <>
      {series.map((s) => (
        <Area
          key={s.key}
          type="monotone"
          dataKey={s.key}
          name={s.label}
          stackId="auth"
          stroke="none"
          fill={hidden.has(s.key) ? "transparent" : SERIES_COLORS[s.key]}
          strokeWidth={0}
          dot={false}
          hide={hidden.has(s.key)}
        />
      ))}
    </>
  );
}

function DayBoundaryLines({
  boundaries,
  theme,
}: {
  readonly boundaries: DayBoundary[];
  readonly theme: Theme;
}) {
  return (
    <>
      {boundaries.map((b) => (
        <ReferenceLine
          key={b.timestamp}
          x={b.timestamp}
          stroke={theme.palette.text.secondary}
          strokeDasharray="4 4"
          strokeOpacity={0.5}
          label={{ value: b.label, position: "insideTopRight", fontSize: 10, fill: theme.palette.text.secondary }}
        />
      ))}
    </>
  );
}

function SeriesLegend({
  series,
  hidden,
  onToggle,
}: {
  readonly series: readonly SeriesDef[];
  readonly hidden: Set<SeriesKey>;
  readonly onToggle: (key: SeriesKey) => void;
}) {
  const theme = useTheme();
  const items: readonly SeriesDef[] = [...series, LINE_SERIES];
  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mt: 1, flexWrap: "wrap" }}>
      {items.map((s) => (
        <Box
          key={s.key}
          onClick={() => onToggle(s.key)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            cursor: "pointer",
            opacity: hidden.has(s.key) ? 0.35 : 1,
          }}
        >
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: SERIES_COLORS[s.key] }} />
          <Box component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
            {s.label}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
